/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";
import PhotoUpload from "@/components/PhotoUpload";
import { Car, CheckCircle2, Loader2, ArrowRight, ChevronLeft, Phone, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function PublicBook() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    address: "",
    license_number: "",
    license_photo: null,
    license_photo_back: null,
    insurance_photo: null,
    rate_type: "weekly",
    pickup_datetime: "",
    return_datetime: "",
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/vehicle/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Vehicle not available."));
  }, [id]);

  const v = data?.vehicle || {};
  const b = data?.business || {};
  const rate = form.rate_type === "weekly" ? Number(v.weekly_rate || 0) : Number(v.daily_rate || 0);

  const totals = useMemo(() => {
    if (!form.pickup_datetime || !form.return_datetime) return { days: 0, total: 0 };
    const p = new Date(form.pickup_datetime);
    const r = new Date(form.return_datetime);
    if (r <= p) return { days: 0, total: 0 };
    const ms = r - p;
    const days = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    if (form.rate_type === "weekly") {
      const weeks = Math.max(1, Math.ceil(days / 7));
      return { days, total: weeks * rate };
    }
    return { days, total: days * rate };
  }, [form, rate]);

  const submit = async () => {
    setErr("");
    if (!form.full_name || !form.phone) return setErr("Name and phone are required");
    if (!form.pickup_datetime) return setErr("Pickup date is required");
    if (form.return_datetime && new Date(form.return_datetime) <= new Date(form.pickup_datetime)) return setErr("Return must be after pickup");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_id: id,
        pickup_datetime: new Date(form.pickup_datetime).toISOString(),
        return_datetime: form.return_datetime ? new Date(form.return_datetime).toISOString() : null,
      };
      const r = await axios.post(`${BACKEND_URL}/api/public/book`, payload);
      setConfirmation(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not complete booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (err && !data) {
    return (
      <CenterCard>
        <h1 className="font-heading text-xl font-semibold mb-1">Unable to load vehicle</h1>
        <p className="text-sm text-gray-600">{err}</p>
        <Link to="/fleet"><Button variant="outline" className="mt-4">Back to fleet</Button></Link>
      </CenterCard>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (confirmation) {
    return (
      <CenterCard>
        <CheckCircle2 className="text-green-500 mx-auto mb-3" size={48} />
        <h1 className="font-heading text-2xl font-bold mb-1">Reservation received!</h1>
        <p className="text-sm text-gray-600">
          We'll text you a confirmation shortly at <strong>{form.phone}</strong>.
        </p>
        <div className="mt-6 bg-gray-50 rounded-lg p-4 text-left">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Booking</div>
          <div className="text-sm">{v.year} {v.make} {v.model}</div>
          <div className="text-sm text-gray-600">{fmtDateTime(form.pickup_datetime)} → {fmtDateTime(form.return_datetime)}</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Total</div>
              <div className="font-semibold tabular-nums">{money(confirmation.total_cost)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Deposit due at pickup</div>
              <div className="font-semibold tabular-nums">{money(confirmation.deposit_required)}</div>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-6">
          Confirmation number: <span className="font-mono">{confirmation.reservation_id.slice(0, 8).toUpperCase()}</span>
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {confirmation.signing_token && (
            <Button
              onClick={() => nav(`/sign/${confirmation.signing_token}`)}
              className="bg-black hover:bg-gray-800 text-white"
              data-testid="goto-signing"
            >
              Sign your rental agreement now <ArrowRight size={14} className="ml-2" />
            </Button>
          )}
          <Link to="/"><Button variant="outline" className="w-full">Back to home</Button></Link>
        </div>
      </CenterCard>
    );
  }

  const fallback = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80";

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="book-nav-home"><Logo /></Link>
          <Link to="/fleet" className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1.5" data-testid="book-back">
            <ChevronLeft size={14} /> Back to fleet
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Vehicle summary */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="aspect-[16/10] bg-gray-100">
              <img src={v.photo || fallback} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="overline">Selected vehicle</div>
              <h2 className="font-heading text-2xl font-bold mt-1">{v.year} {v.make} {v.model}</h2>
              <div className="text-xs text-gray-500 mt-1 font-mono">Plate {v.plate || "—"}</div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-heading text-xl font-bold tabular-nums">{money(v.weekly_rate)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">per week</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-heading text-xl font-bold tabular-nums">{money(v.deposit_amount)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">deposit</div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5 text-sm space-y-3">
            <div className="flex items-center gap-2.5"><ShieldCheck className="text-amber-600" size={16} /> Insurance-ready · GPS-equipped</div>
            <div className="flex items-center gap-2.5"><Phone className="text-amber-600" size={16} /> {b.phone || "(215) 770-0679"}</div>
          </div>
        </div>

        {/* Booking form */}
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-5 md:p-7">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight mb-1">Reserve your rental</h1>
          <p className="text-sm text-gray-600 mb-6">Fill in your details — we'll text you a confirmation.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Full name *</Label>
              <Input data-testid="book-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="As shown on your driver's license" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input data-testid="book-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+12157700679" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="book-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input data-testid="book-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, City, ZIP" />
            </div>
            <div>
              <Label>Driver license #</Label>
              <Input data-testid="book-license" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
            </div>
            <div>
              <Label>Rate type</Label>
              <Select value={form.rate_type} onValueChange={(v) => setForm({ ...form, rate_type: v })}>
                <SelectTrigger data-testid="book-rate-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly ({money(v.weekly_rate || 0)})</SelectItem>
                  <SelectItem value="daily">Daily ({money(v.daily_rate || 0)})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pickup date/time *</Label>
              <Input data-testid="book-pickup" type="datetime-local" value={form.pickup_datetime} onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })} />
            </div>
            <div>
              <Label>Return date/time (optional)</Label>
              <Input data-testid="book-return" type="datetime-local" value={form.return_datetime} onChange={(e) => setForm({ ...form, return_datetime: e.target.value })} />
            </div>
          </div>

          {/* ID UPLOAD */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="mb-3">
              <div className="overline mb-1">Driver's license</div>
              <p className="text-xs text-gray-500">
                Upload photos of the front and back of your driver's license. On mobile this opens your camera. Optional now — we'll ask again at pickup if missing.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PhotoUpload
                testId="book-license-front"
                label="License — front"
                hint="JPG or PNG, up to ~5MB"
                capture
                value={form.license_photo}
                onChange={(v) => setForm({ ...form, license_photo: v })}
              />
              <PhotoUpload
                testId="book-license-back"
                label="License — back"
                hint="JPG or PNG, up to ~5MB"
                capture
                value={form.license_photo_back}
                onChange={(v) => setForm({ ...form, license_photo_back: v })}
              />
            </div>
            <div className="mt-3">
              <PhotoUpload
                testId="book-insurance"
                label="Insurance card (optional)"
                hint="Helps speed up pickup"
                capture
                value={form.insurance_photo}
                onChange={(v) => setForm({ ...form, insurance_photo: v })}
              />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="overline">Duration</div>
              <div className="font-heading text-lg font-bold tabular-nums mt-1">{totals.days} day{totals.days !== 1 ? "s" : ""}</div>
            </div>
            <div>
              <div className="overline">Estimated total</div>
              <div className="font-heading text-lg font-bold tabular-nums mt-1" data-testid="book-total">{money(totals.total)}</div>
            </div>
            <div>
              <div className="overline">Deposit due</div>
              <div className="font-heading text-lg font-bold tabular-nums mt-1">{money(v.deposit_amount)}</div>
            </div>
          </div>

          {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="book-error">{err}</div>}

          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="book-submit"
            className="w-full mt-6 bg-black hover:bg-gray-800 text-white"
            size="lg"
          >
            {submitting ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Booking…</>) : (<>Reserve now <ArrowRight size={14} className="ml-2" /></>)}
          </Button>
          <p className="text-xs text-gray-500 mt-3 text-center">
            No payment due now. Deposit collected at pickup. Cancel anytime up to 24h before pickup.
          </p>
        </div>
      </div>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8 text-center">{children}</div>
    </div>
  );
}
